import React, { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    CalendarRange, ClipboardCheck, Clock, Coins, FileText, FolderKanban, History, Images, Key, KeyRound,
    Info, ListTodo, LucideIcon, Radio, StickyNote, Users, Wallet, Wrench, FolderOpen, Map as MapIcon,
    TriangleAlert, Biohazard, HardHat, Construction, DoorOpen, Car, Warehouse,
} from 'lucide-react';
import { Building } from '../../../types/protocols';
import ExpandableText from '../components/ExpandableText';
import noReception from '../../../Images/no_reception.png';
import { useFeature } from '../../../hooks/useFeatures';
import { useTenantTerm } from '../../../hooks/useTenantTerm';
import CategoryCard from './CategoryCard';
import {
    CategoryView,
    RiskCounts,
    VIEW_PARAM,
    buildingPaths,
    parseView,
    tabBadge,
    visibleTabs,
} from './buildingCategoryTabs';

interface CategoryTabsProps {
    building: Building;
    buildingKey: string;
    isAdmin: boolean;
    todoCount: number;
    risks: RiskCounts;
}

const TAB_ICONS: Record<CategoryView, LucideIcon> = {
    allmant: Info,
    underhall: Wrench,
    ekonomi: Wallet,
    dokumentation: FolderOpen,
    risker: TriangleAlert,
};

/**
 * The building page's category tabs: a tab list on the left (a scrolling bar
 * on phones) and the active category's cards on the right. The active tab
 * lives in the URL (`?vy=`) so links reproduce it.
 */
const CategoryTabs: React.FC<CategoryTabsProps> = ({ building, buildingKey, isAdmin, todoCount, risks }) => {
    const [params, setParams] = useSearchParams();
    const info = building.summary.buildingInfo;
    const sources = { todoCount, hasPoorReception: Boolean(info?.hasPoorReception), risks };
    const tabs = visibleTabs(sources);
    const view = parseView(params.get(VIEW_PARAM), tabs);
    const tablistRef = useRef<HTMLDivElement>(null);
    const tenantsEnabled = useFeature('tenants');
    const tenantTerm = useTenantTerm();
    const keyRegisterEnabled = useFeature('keyRegister');
    const unitsEnabled = useFeature('units');
    const parkingEnabled = useFeature('parking');
    const storageEnabled = useFeature('storageUnits');

    const changeView = (next: CategoryView) => {
        setParams(previous => {
            const updated = new URLSearchParams(previous);
            updated.set(VIEW_PARAM, next);
            return updated;
        }, { replace: true });
    };

    // On phones the tab list scrolls sideways; keep the active tab in view without scrolling the page.
    useEffect(() => {
        const bar = tablistRef.current;
        const active = bar?.querySelector<HTMLElement>('.bp-tab--active');
        if (!bar || !active) return;
        const left = active.offsetLeft - bar.offsetLeft;
        if (left < bar.scrollLeft || left + active.offsetWidth > bar.scrollLeft + bar.clientWidth) {
            bar.scrollLeft = left - 16;
        }
    }, [view]);

    const paths = buildingPaths(buildingKey);

    const renderCards = () => {
        switch (view) {
            case 'underhall':
                return (
                    <>
                        <CategoryCard to={paths.maintenance} Icon={CalendarRange} label="Underhållsplan"
                            description="Planerade åtgärder och kostnader per år" />
                        <CategoryCard to={paths.checks} Icon={ClipboardCheck} label="Kontroller"
                            description="Kontroller, mätaravläsningar och service" />
                        <CategoryCard to={paths.projects} Icon={FolderKanban} label="Projekt"
                            description="Pågående och avslutade projekt" />
                        <CategoryCard to={paths.todos} Icon={ListTodo} label="Att göra"
                            description="Öppna uppgifter för byggnaden" />
                    </>
                );
            case 'ekonomi':
                return (
                    <>
                        {isAdmin && tenantsEnabled && (
                            <CategoryCard to={paths.leases} Icon={KeyRound} label="Hyresavtal"
                                description="Avtal för byggnadens uthyrningsbara objekt" />
                        )}
                        {isAdmin && tenantsEnabled && (
                            <CategoryCard to={paths.tenants} Icon={Users} label={tenantTerm.plural}
                                description={`${tenantTerm.pluralInText} med avtal i byggnaden, nu och tidigare`} />
                        )}
                        {unitsEnabled && (
                            <CategoryCard to={paths.units} Icon={DoorOpen} label="Lägenheter & lokaler"
                                description="Byggnadens lägenheter och lokaler" />
                        )}
                        {parkingEnabled && (
                            <CategoryCard to={paths.parking} Icon={Car} label="Parkering"
                                description="Byggnadens parkeringsområden och platser" />
                        )}
                        {storageEnabled && (
                            <CategoryCard to={paths.storageUnits} Icon={Warehouse} label="Förråd"
                                description="Byggnadens förråd" />
                        )}
                        <CategoryCard to={paths.opCosts} Icon={Coins} label="Driftkostnader"
                            description="Kostnader per år och kategori" />
                        {isAdmin && keyRegisterEnabled && (
                            <CategoryCard to={paths.keys} Icon={Key} label="Nycklar"
                                description="Nycklar och taggar som går till byggnaden" />
                        )}
                    </>
                );
            case 'dokumentation':
                return (
                    <>
                        <CategoryCard to={paths.images} Icon={Images} label="Samtliga bilder"
                            description="Alla bilder från byggnaden" />
                        <CategoryCard to={paths.documents} Icon={FileText} label="Dokument & anteckningar"
                            description="Filer och anteckningar för byggnaden" />
                        <CategoryCard to={paths.timeline} Icon={History} label="Tidslinje"
                            description="Händelser för byggnaden över tid" />
                    </>
                );
            case 'allmant':
                return (
                    <>
                        {info?.hasPoorReception && (
                            <CategoryCard Icon={Radio} label="Mobiltäckning eller wifi">
                                <span className="bp-card-reception">
                                    <img src={noReception} alt="" />
                                    Ingen mottagning
                                </span>
                                <span className="bp-card-sub">
                                    Det finns ingen (eller väldigt bristfällig) mobiltäckning vid byggnaden. Viktigt att använda off grid-mode vid användning av Molnfastigheters app vid denna byggnad.
                                </span>
                            </CategoryCard>
                        )}
                        <CategoryCard Icon={Key} label="Nyckelinstruktioner">
                            <ExpandableText text={info?.keyInstructions} title="Nyckelinstruktioner" />
                        </CategoryCard>
                        {info?.seasonInfo?.comment && (
                            <CategoryCard Icon={Clock} label="Öppningsperiod">
                                <span style={{ whiteSpace: 'pre-wrap' }}>{info.seasonInfo.comment}</span>
                            </CategoryCard>
                        )}
                        <CategoryCard Icon={StickyNote} label="Övrigt">
                            <ExpandableText text={info?.other} title="Övrigt" />
                        </CategoryCard>
                        <CategoryCard to={paths.contacts} Icon={Users} label="Externa kontakter"
                            description="Entreprenörer, leverantörer och andra kontakter" />
                        <CategoryCard to={paths.gps} Icon={MapIcon} label="GPS-karta"
                            description="Komponenternas positioner på kartan" />
                    </>
                );
            case 'risker':
                // One card per kind the building has; every card opens the risks overview.
                return (
                    <>
                        {risks.hazards > 0 && (
                            <CategoryCard to={paths.risks} Icon={Biohazard} label="Miljö- och hälsofara"
                                description="Farliga ämnen i komponenter" />
                        )}
                        {risks.riskConstructions > 0 && (
                            <CategoryCard to={paths.risks} Icon={Construction} label="Riskkonstruktioner"
                                description="Konstruktioner med känd risk" />
                        )}
                        {risks.workEnvironment > 0 && (
                            <CategoryCard to={paths.risks} Icon={HardHat} label="Arbetsmiljörisker"
                                description="Risker vid arbete i byggnaden" />
                        )}
                    </>
                );
        }
    };

    return (
        <div className="bp-categories">
            <div className="bp-tablist" role="tablist" aria-label="Kategori" ref={tablistRef}>
                {tabs.map(tab => {
                    const Icon = TAB_ICONS[tab.key];
                    const badge = tabBadge(tab.key, sources);
                    const active = tab.key === view;
                    return (
                        <button
                            key={tab.key}
                            type="button"
                            role="tab"
                            aria-selected={active}
                            className={`bp-tab${active ? ' bp-tab--active' : ''}`}
                            onClick={() => changeView(tab.key)}
                        >
                            <Icon size={18} aria-hidden="true" />
                            <span className="bp-tab-label">{tab.label}</span>
                            {badge && <span className="bp-tab-badge">{badge}</span>}
                        </button>
                    );
                })}
            </div>
            <div className="bp-cards" role="tabpanel">
                {renderCards()}
            </div>
        </div>
    );
};

export default CategoryTabs;
